import { captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import { createParticipantLink, type ParticipantLinkMinted } from '@/core/api/portal'
import type { FormValues } from '@/core/components/form/formModel'
import type { PendingErstgespraech } from '@/core/state/appStore'
import {
  createCoachingForPerson,
  updateParticipantStammdaten,
  type ParticipantStammdaten,
} from '@/core/data/coachings'
import { getDocData, upsertDoc } from '@/core/data/dokumente'
import { validatePresence, findMalformedEmails, type PresenceRules } from '@/core/lib/validate'
import { AUFGABE } from '@/core/data/aufgabe'
import type { DashboardCoaching } from '@/core/types'
import type { FieldError, FieldErrors } from '@/core/lib/fieldError'
import type { FieldId } from '@/core/lib/result'

const ERST = 'erstgespraech'

function stammdatenFromForm(data: FormValues): ParticipantStammdaten {
  const t = (data.Teilnehmer ?? {}) as Record<string, unknown>
  return {
    vorname: String(t['Vorname'] ?? '').trim(),
    nachname: String(t['Nachname'] ?? '').trim(),
    email: String(t['E-Mail'] ?? '').trim(),
  }
}

export async function persistErstgespraech(coachingId: string, data: FormValues): Promise<void> {
  await updateParticipantStammdaten(coachingId, stammdatenFromForm(data))
  await upsertDoc(coachingId, ERST, { daten: { checkliste: data } })
}

export function erstgespraechCardName(data: FormValues): string | null {
  const { nachname, vorname } = stammdatenFromForm(data)
  return nachname && vorname ? `${nachname}, ${vorname}` : null
}

export function newErstgespraechCard(pending: PendingErstgespraech): DashboardCoaching {
  return {
    id: pending.coachingId,
    name: erstgespraechCardName(pending.values),
    stage: 'avgs',
    documents: [ERST],
    sheets: [],
    sitzungDaten: {},
    docCount: 1,
    ueGesamt: null,
    ueStattgefunden: 0,
    coach: '',
    coachId: null,
    kostentraeger: '',
    kostentraegerId: null,
    gueltigkeitBeginn: null,
    gueltigkeitEnde: null,
    beginn: null,
    aufgabe: AUFGABE.stammdaten.dbName,
  }
}

export function advanceNewErstgespraech(pending: PendingErstgespraech): Promise<ParticipantLinkMinted> {
  if (advanceNewInFlight?.coachingId !== pending.coachingId) {
    const promise = doAdvanceNew(pending).finally(() => {
      if (advanceNewInFlight?.promise === promise) advanceNewInFlight = null
    })
    advanceNewInFlight = { coachingId: pending.coachingId, promise }
  }
  return advanceNewInFlight.promise
}
let advanceNewInFlight: { coachingId: string; promise: Promise<ParticipantLinkMinted> } | null = null

async function doAdvanceNew(pending: PendingErstgespraech): Promise<ParticipantLinkMinted> {
  const { values: data, coachingId, teilnehmendeId } = pending
  const teilnehmer = (data.Teilnehmer ?? {}) as Record<string, unknown>
  const nachname = String(teilnehmer.Nachname ?? '').trim()
  const vorname = String(teilnehmer.Vorname ?? '').trim()
  await createCoachingForPerson(coachingId, nachname, vorname, stammdatenFromForm(data), teilnehmendeId)
  await upsertDoc(coachingId, ERST, { daten: { checkliste: data } })
  return createParticipantLink(coachingId, 'stammdaten')
}

export async function advanceExistingErstgespraech(
  coachingId: string,
  data: FormValues,
): Promise<ParticipantLinkMinted> {
  await persistErstgespraech(coachingId, data)
  return createParticipantLink(coachingId, 'stammdaten')
}

const VORAUSSETZUNG_ROWS = [
  'Deutschsprachkenntnisse A2/B1',
  'Online-Beratungsgespräch durchführen',
  'Technische Ausstattung',
] as const

function legacyCellAffirmative(v: unknown): boolean {
  return v === true || v === 'ja'
}

function collapseLegacyRow(fields: Record<string, unknown>): string {
  if (legacyCellAffirmative(fields['Nachweis vorhanden?'])) return "Nachweis vorhanden"
  if (legacyCellAffirmative(fields['Nachweis notwendig?'])) return "Nachweis notwendig"
  if (legacyCellAffirmative(fields['erfüllt?'])) return "erfüllt"
  return ''
}

export function adaptLegacyVoraussetzungen<T extends Record<string, unknown> | null | undefined>(
  saved: T,
): T {
  if (!saved) return saved
  let changed = false
  const out: Record<string, unknown> = { ...saved }
  let voraussetzungen: Record<string, unknown> | undefined
  for (const row of VORAUSSETZUNG_ROWS) {
    const raw = saved[row]
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    if (!voraussetzungen) {
      const existing = out.Voraussetzungen
      voraussetzungen = { ...(existing && typeof existing === 'object' ? existing : {}) }
    }
    voraussetzungen[row] = collapseLegacyRow(raw as Record<string, unknown>)
    delete out[row]
    changed = true
  }
  if (voraussetzungen) out.Voraussetzungen = voraussetzungen
  return (changed ? out : saved) as T
}

export async function loadErstgespraech(
  coachingId: string,
): Promise<Record<string, unknown> | null> {
  const d = await getDocData(coachingId, ERST)
  return (d?.checkliste as Record<string, unknown> | undefined) ?? null
}

export async function saveErstgespraechDraft(
  coachingId: string,
  data: FormValues,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  await upsertDoc(coachingId, ERST, { daten: { checkliste: data } }, identity)
}

export type ErstgespraechCheck = { errors: FieldErrors }

const erstgespraechPresenceRules: PresenceRules = {
  required: [
    'Teilnehmer.Nachname',
    'Teilnehmer.Vorname',
    'Teilnehmer.E-Mail',
    'Maßnahme.Datum des Gesprächs',
    'Gesprächsführer.Name, Vorname',
  ],
}

export const erstgespraechManualFields: ReadonlySet<FieldId> = new Set(
  erstgespraechPresenceRules.required,
)

const ERSTGESPRAECH_EMAIL_PATHS: FieldId[] = ['Teilnehmer.E-Mail']

export function checkErstgespraech(values: Record<string, unknown>): ErstgespraechCheck {
  const { missing } = validatePresence(values, erstgespraechPresenceRules)
  const errors = new Map<FieldId, FieldError>()
  for (const path of missing) errors.set(path, { kind: 'missing' })
  for (const path of findMalformedEmails(values, ERSTGESPRAECH_EMAIL_PATHS)) {
    if (!errors.has(path)) errors.set(path, { kind: 'malformed', expected: 'email' })
  }
  return { errors }
}
